import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: number;
  name: string;
  email: string;
  locale: string;
  timezone: string;
  avatar_path?: string | null;
  /** Mirrors the server's `Gate::before` bypass. Never true for a business role. */
  is_platform_admin?: boolean;
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: User | null;
  tenants: TenantSummary[];
  currentTenantId: string | null;
  permissions: string[];
  roles: string[];
  enabledModules: string[];

  setSession: (data: {
    user: User;
    tenants: TenantSummary[];
    currentTenantId?: string | null;
    permissions?: string[];
    roles?: string[];
    enabledModules?: string[];
  }) => void;

  switchTenant: (tenantId: string) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenants: [],
      currentTenantId: null,
      permissions: [],
      roles: [],
      enabledModules: [],

      setSession: (data) => {
        if (data.currentTenantId) {
          localStorage.setItem('current_tenant_id', data.currentTenantId);
        }
        set({
          user: data.user,
          tenants: data.tenants,
          currentTenantId: data.currentTenantId ?? data.tenants[0]?.id ?? null,
          permissions: data.permissions ?? [],
          roles: data.roles ?? [],
          enabledModules: data.enabledModules ?? [],
        });
      },

      switchTenant: (tenantId) => {
        localStorage.setItem('current_tenant_id', tenantId);
        set({ currentTenantId: tenantId });
      },

      clear: () => {
        localStorage.removeItem('current_tenant_id');
        set({
          user: null,
          tenants: [],
          currentTenantId: null,
          permissions: [],
          roles: [],
          enabledModules: [],
        });
      },
    }),
    { name: 'erp_auth' },
  ),
);

/**
 * Does this user hold an ability?
 *
 * Reads the permission list the API returns from `/me`, which already includes
 * anything held only through a live temporary access grant — so a screen
 * unlocked by a grant re-locks by itself when the grant lapses, in step with
 * the API rather than after a reload.
 *
 * The previous implementation also passed anyone holding a role named
 * `super-admin`. No such role exists in this system, so that branch never
 * fired; platform administrators are recognised by their own flag instead,
 * which is the same thing the server's `Gate::before` checks.
 */
export function useCan(permission: string): boolean {
  return useAuth((s) => s.user?.is_platform_admin === true || s.permissions.includes(permission));
}

/** Every ability the user holds. Handy for passing to `filterNav`. */
export function usePermissions(): string[] {
  return useAuth((s) => s.permissions);
}

export function useModuleEnabled(moduleKey: string): boolean {
  return useAuth((s) => s.enabledModules.includes(moduleKey.toLowerCase()));
}
